/**
 * Revocation lifecycle (MTN-1443).
 *
 * On a full-page-reload site the controlled reload is the teardown: when the
 * visitor makes a restrictive consent change (a previously allowed Termly
 * category disappears, or do_not_sell turns on), this module runs the
 * registered purge hooks (the storage adapters that own the actual deletion
 * live in the storage manifest — MTN-1444), then reloads the page once —
 * killing timers, listeners, SDKs, and iframes without per-consumer
 * teardown code — but only when the revoked categories left vendor code
 * running that has no in-place downgrade (see needsReload).
 *
 * Each purge hook also runs once at page init, after Termly resolves, so a
 * straggler write that landed between revoke and reload is deleted on the
 * next load. An unresolved Termly is not a denial, so no purge runs then;
 * consent-gated readers already return nothing while unresolved.
 *
 * In-flight writes need no machinery here: window.termlyData updates
 * synchronously before the consent event dispatches, so the storage
 * adapter re-checks the entry's category immediately before committing and
 * drops the write when it changed mid-flight (MTN-1444).
 *
 * Vendor opt-outs stay where they are: PostHog opt-out and Tune-decoration
 * removal run from their own watchCategory handlers on the same
 * termlyDataUpdate event. Advertising-cookie deletion belongs to the
 * storage-manifest purge hook (MTN-1444).
 */

import { captureException } from "./posthog";
import { CATEGORIES, ready } from "./termly";

// The `consent` event is itself the overlay-closed signal, so no DOM
// watching is needed. Termly's embed dispatches exactly two events,
// `initialized` and `consent` (verified in the shipped resource-blocker
// source, 2026-08-13); the overlay closes synchronously on the save click,
// while `consent` dispatches only after the save POST to Termly's API
// resolves — verified live 2026-08-13: at the instant `consent` fired, the
// overlay was already out of the DOM.
//
// Termly also reloads the page itself — synchronously, right after
// dispatching `consent` — whenever a previously allowed category is denied
// AND the page contains a matching `data-categories` blocked element (same
// source). Our delayed reload is the fallback for pages where that markup
// is absent; when Termly reloads first, ours never runs, the pending purge
// is cut short, and the init purge-check on the next load finishes the
// deletion. The grace delay exists only to let Termly's own reload win and
// any closing animation settle.
const RELOAD_GRACE_MS = 1000;
const PURGE_WAIT_CAP_MS = 5000;
const RELOAD_GUARD_KEY = "mtn-consent-reload";
const RELOAD_GUARD_WINDOW_MS = 30_000;

const purgeHooks = new Set();

const runHook = async (hook) => {
  try {
    await hook();
  } catch (error) {
    console.error(error);
    captureException(error, { source: "consent-lifecycle:purge" });
  }
};

/**
 * Register an idempotent purge hook. It runs on every restrictive consent
 * change and once at page init after Termly resolves (the init purge-check).
 * Hooks live for the page's lifetime; the reload is the teardown.
 *
 * @param {function} hook - Deletes storage its owner holds without consent.
 */
export const registerPurgeHook = (hook) => {
  purgeHooks.add(hook);
  ready(() => runHook(hook));
};

const runPurgeHooks = () => Promise.all([...purgeHooks].map(runHook));

let reloadScheduled = false;

const scheduleReload = () => {
  if (reloadScheduled) {
    return;
  }
  reloadScheduled = true;

  // Reload-loop guard (chunk-reload precedent): a second restrictive change
  // within the window purges but does not reload again, and if the guard
  // can't persist the reload is skipped entirely — read gating and the init
  // purge sweep still apply either way.
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    return;
  }

  setTimeout(() => window.location.reload(), RELOAD_GRACE_MS);
};

const snapshot = () => ({
  categories: window.termlyData?.categories || [],
  doNotSell: Boolean(window.termlyData?.consentState?.do_not_sell),
});

/**
 * A reload is the teardown only for revoked categories whose vendor code
 * has no in-place downgrade; everything else respects the new preference
 * without one (purge, read gating, and commit-time re-checks always run):
 *
 * - Advertising, or do_not_sell turning on: the Meta pixel initializes on
 *   every page while Advertising is granted (plus Quantcast/SmartAsset/ads),
 *   and none expose an un-init — and "revoked" implies it was granted on
 *   this page, so the pixel is live. Always reload.
 * - Performance: Tally's embed has no un-init. Reload only while it is live.
 * - Analytics: PostHog opts out via its own watchCategory handler and Google
 *   downgrades through the Consent Mode bridge — official in-place paths.
 * - Social Networking / Unclassified: only data-categories iframes, which is
 *   exactly the case where Termly reloads the page itself.
 *
 * Adding an allow* consumer that initializes vendor code with no in-place
 * teardown means updating this policy (the consent-reload-policy scan test
 * fails on unacknowledged consumers).
 */
const needsReload = (revoked, dnsFlipped) => {
  if (dnsFlipped || revoked.includes(CATEGORIES.ADVERTISING)) {
    return true;
  }
  if (revoked.includes(CATEGORIES.PERFORMANCE)) {
    return (
      window.Tally !== undefined ||
      document.querySelector(
        'script[src="https://tally.so/widgets/embed.js"]'
      ) !== null
    );
  }
  return false;
};

let installed = false;

export const initConsentLifecycle = () => {
  if (installed) {
    return;
  }
  installed = true;

  // Termly's initial report is a starting state, not a change: only a
  // transition observed within this page's lifetime counts as restrictive.
  let prev = window.termlyData === null ? null : snapshot();

  document.addEventListener("termlyDataUpdate", () => {
    const next = snapshot();
    const previous = prev;
    prev = next;
    if (previous === null) {
      return;
    }

    const kept = new Set(next.categories);
    const revoked = previous.categories.filter(
      (category) => !kept.has(category)
    );
    const dnsFlipped = next.doNotSell && !previous.doNotSell;
    if (revoked.length === 0 && !dnsFlipped) {
      return;
    }

    if (!needsReload(revoked, dnsFlipped)) {
      // Everything on this page already respects the downgrade in place;
      // the purge sweep is the whole teardown.
      runPurgeHooks();
      return;
    }

    // Purge before reloading so the fresh page lands on cleaned storage, but
    // cap the wait: a hung hook must not block the teardown reload. The init
    // sweep on the next load deletes stragglers.
    Promise.race([
      runPurgeHooks(),
      new Promise((resolve) => setTimeout(resolve, PURGE_WAIT_CAP_MS)),
    ]).then(scheduleReload);
  });
};
